import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  DollarSign, 
  Package, 
  CheckCircle, 
  Clock, 
  XCircle,
  Edit,
  Trash2,
  ShoppingCart,
  FileText,
  CreditCard,
  Upload,
  Eye,
  AlertTriangle,
  Copy,
  Sparkles,
  Printer
} from 'lucide-react';

// Componentes reutilizables
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';

// Servicios
import salesService from '../services/salesService';
import proposalService from '../services/proposalService';
import uploadService from '../services/uploadService';
import projectService from '../services/projectService';
import { printSaleDocument } from '../utils/printUtils';
import { API_CONFIG } from '../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

const SaleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();
  const documentsSectionRef = useRef(null);
  const billingSectionRef = useRef(null);

  // Estados
  const [sale, setSale] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [missingDocs, setMissingDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  
  // Estados para facturación y pagos
  const [billingData, setBillingData] = useState({
    startDate: '',
    endDate: ''
  });
  const [paymentPlan, setPaymentPlan] = useState({
    type: '100%',
    details: {
      initialPaymentPercent: 100,
      installments: 1
    }
  });
  const [showCustomPlan, setShowCustomPlan] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);

  // Mapeo de tipos de documentos
  const docTypeLabels = {
    'COTIZACION': 'Cotización Final',
    'DIMENSIONAMIENTO': 'Documento de Dimensionamiento',
    'DOC_TECNICO': 'Documento Técnico'
  };

  // Cargar venta
  const loadSale = useCallback(async () => {
    try {
      setLoading(true);
      const response = await salesService.getSaleById(id);
      setSale(response.sale);
      
      // Cargar datos de facturación si existen
      if (response.sale.billingData) {
        setBillingData({
          startDate: response.sale.billingData.startDate ? new Date(response.sale.billingData.startDate).toISOString().split('T')[0] : '',
          endDate: response.sale.billingData.endDate ? new Date(response.sale.billingData.endDate).toISOString().split('T')[0] : ''
        });
      }
      
      // Cargar plan de pagos si existe
      if (response.sale.paymentPlan) {
        setPaymentPlan(response.sale.paymentPlan);
        setShowCustomPlan(response.sale.paymentPlan.type === 'CUSTOM');
      }

      // Si la venta viene de una propuesta AI, cargamos los documentos
      if (response.sale.proposalId) {
        try {
          const propData = await proposalService.getProposalById(response.sale.proposalId);
          if (propData.proposal) setProposal(propData.proposal);
        } catch(e) {
          console.warn("No se pudo cargar la propuesta asociada a la venta.");
        }
      }

      calculateRequirements(response.sale);
    } catch (error) {
      console.error('Error cargando venta:', error);
      showNotification('Error al cargar la venta', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showNotification]);

  // Calcular documentos requeridos y faltantes
  const calculateRequirements = (saleData) => {
    if (!saleData || !saleData.items) return;

    const allRequiredDocs = new Set();
    saleData.items.forEach(item => {
      if (item.service && item.service.category && item.service.category.requiredDocuments) {
        item.service.category.requiredDocuments.forEach(doc => allRequiredDocs.add(doc));
      }
    });

    const requiredList = Array.from(allRequiredDocs);
    setRequiredDocs(requiredList);

    const uploadedTypes = saleData.documents?.map(d => d.type) || [];
    const missing = requiredList.filter(doc => !uploadedTypes.includes(doc));
    setMissingDocs(missing);
  };

  // Eliminar venta
  const handleDelete = async () => {
    const confirmed = window.confirm(
      `¿Estás seguro de que quieres cancelar esta venta?\n\nEsta acción cambiará el estado a "Cancelada".`
    );
    
    if (confirmed) {
      try {
        await salesService.deleteSale(id);
        showNotification('Venta cancelada exitosamente', 'success');
        navigate('/sales');
      } catch (error) {
        console.error('Error eliminando venta:', error);
        showNotification('Error al cancelar la venta', 'error');
      }
    }
  };

  // Manejar subida de archivo
  const handleFileUpload = async (event, docType) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de archivo en el frontend
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      showNotification('Formato no válido. Solo se permiten archivos PDF, JPG y PNG.', 'error');
      event.target.value = ''; // Limpiar el input
      return;
    }

    // Validar tamaño (ejemplo: 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      showNotification('El archivo es demasiado grande. El tamaño máximo es 5MB.', 'error');
      event.target.value = '';
      return;
    }

    try {
      setUploadingDoc(docType);
      
      // 1. Subir archivo al servidor
      const uploadResponse = await uploadService.uploadFile(file);
      
      // 2. Actualizar venta con la referencia del documento
      const documentData = {
        type: docType,
        url: uploadResponse.url,
        filename: uploadResponse.originalname
      };

      // Clonar documentos actuales y agregar/actualizar el nuevo
      const currentDocuments = sale.documents || [];
      // Filtrar si ya existe uno del mismo tipo para reemplazarlo
      const updatedDocuments = [
        ...currentDocuments.filter(d => d.type !== docType),
        documentData
      ];

      await salesService.updateSale(id, { documents: updatedDocuments });
      
      showNotification('Documento subido exitosamente', 'success');
      
      // Recargar venta para actualizar estado
      loadSale();

    } catch (error) {
      console.error('Error subiendo documento:', error);
      // Mostrar mensaje de error específico del servidor si existe
      const errorMessage = error.message || 'Error al subir documento';
      if (errorMessage.includes('PDF e imágenes')) {
        showNotification('El servidor rechazó el archivo: Solo se permiten PDF, JPG y PNG.', 'error');
      } else {
        showNotification(errorMessage, 'error');
      }
    } finally {
      setUploadingDoc(null);
      // Limpiar input
      event.target.value = '';
    }
  };

  // Manejar cambio de plan de pagos
  const handlePaymentPlanChange = (type) => {
    const plans = {
      '100%': { initialPaymentPercent: 100, installments: 1 }, // 1 pago único
      '50/50': { initialPaymentPercent: 50, installments: 1 }, // 50% inicial + 1 pago del 50%
      '40/30/30': { initialPaymentPercent: 40, installments: 2 }, // 40% inicial + 2 pagos del 30%
      'CUSTOM': { initialPaymentPercent: 0, installments: 1 }
    };

    setPaymentPlan({
      type,
      details: plans[type] || { initialPaymentPercent: 100, installments: 1 }
    });
    setShowCustomPlan(type === 'CUSTOM');
  };

  // Confirmar venta
  const handleConfirmSale = async () => {
    // Validar documentos
    if (missingDocs.length > 0) {
      showNotification(`Faltan documentos requeridos: ${missingDocs.map(d => docTypeLabels[d]).join(', ')}`, 'warning');
      // Scroll al bloque de documentos
      if (documentsSectionRef.current) {
        documentsSectionRef.current.scrollIntoView({ behavior: 'smooth' });
        documentsSectionRef.current.classList.add('ring-2', 'ring-red-500', 'ring-offset-2');
        setTimeout(() => {
          documentsSectionRef.current?.classList.remove('ring-2', 'ring-red-500', 'ring-offset-2');
        }, 2000);
      }
      return;
    }

    // Validar datos de facturación
    if (!billingData.startDate || !billingData.endDate) {
      showNotification('Debes completar las fechas de facturación', 'warning');
      // Scroll al bloque de facturación
      if (billingSectionRef.current) {
        billingSectionRef.current.scrollIntoView({ behavior: 'smooth' });
        billingSectionRef.current.classList.add('ring-2', 'ring-red-500', 'ring-offset-2');
        setTimeout(() => {
          billingSectionRef.current?.classList.remove('ring-2', 'ring-red-500', 'ring-offset-2');
        }, 2000);
      }
      return;
    }

    if (new Date(billingData.endDate) < new Date(billingData.startDate)) {
      showNotification('La fecha de fin no puede ser anterior a la de inicio', 'error');
      return;
    }

    // Validar plan personalizado
    if (paymentPlan.type === 'CUSTOM') {
      if (paymentPlan.details.initialPaymentPercent <= 0 || paymentPlan.details.initialPaymentPercent > 100) {
        showNotification('El porcentaje inicial debe ser mayor a 0 y menor o igual a 100', 'error');
        return;
      }
      if (paymentPlan.details.installments < 1) {
        showNotification('El número de cuotas debe ser al menos 1', 'error');
        return;
      }
    }

    try {
      await salesService.updateSale(id, { 
        status: 'CONFIRMED',
        billingData,
        paymentPlan
      });
      showNotification('Venta confirmada y factura generada exitosamente', 'success');
      loadSale();
    } catch (error) {
      console.error('Error confirmando venta:', error);
      showNotification('Error al confirmar la venta', 'error');
    }
  };

  // Obtener estado de la venta
  const getSaleStatus = (status) => {
    const statusConfig = {
      'PENDING': { label: 'Pendiente', icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50' },
      'CONFIRMED': { label: 'Confirmada', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
      'CANCELLED': { label: 'Cancelada', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
      'COMPLETED': { label: 'Completada', icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50' }
    };
    return statusConfig[status] || statusConfig['PENDING'];
  };

  // Obtener estado de pago
  const getPaymentStatus = (status) => {
    const statusConfig = {
      'PENDING': { label: 'Pendiente', color: 'text-yellow-600', bg: 'bg-yellow-50' },
      'PAID': { label: 'Pagado', color: 'text-green-600', bg: 'bg-green-50' },
      'PARTIAL': { label: 'Pago Parcial', color: 'text-blue-600', bg: 'bg-blue-50' },
      'REFUNDED': { label: 'Reembolsado', color: 'text-red-600', bg: 'bg-red-50' }
    };
    return statusConfig[status] || statusConfig['PENDING'];
  };

  // Obtener método de pago
  const getPaymentMethod = (method) => {
    const methods = {
      'CASH': 'Efectivo',
      'CARD': 'Tarjeta',
      'TRANSFER': 'Transferencia',
      'OTHER': 'Otro'
    };
    return methods[method] || 'No especificado';
  };

  // Función de impresión de documentos Markdown (Reutilizada de Proposal Detail)
  const printMarkdownDocument = (title, content) => {
    const printWin = window.open('', '', 'width=800,height=600');
    if (!printWin) return;
    const safeContent = content.replace(/\\/g, '\\\\').replace(/`/g, '\\`');
    const baseUrl = window.location.origin;

    printWin.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
            .header-container { display: flex; align-items: center; border-bottom: 2px solid #4f7942; padding-bottom: 15px; margin-bottom: 25px; }
            .header-container img { max-height: 50px; margin-right: 20px; }
            .header-container h2 { color: #4f7942; margin: 0; font-size: 24px; }
            h1, h2, h3, h4 { color: #1f2937; margin-top: 24px; margin-bottom: 12px; }
            p { margin-bottom: 12px; }
            ul, ol { margin-left: 24px; margin-bottom: 16px; }
            li { margin-bottom: 6px; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 20px; font-size: 14px; }
            th, td { border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; }
            th { background-color: #f8fafc; font-weight: bold; }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/marked/marked.min.js"></script>
        </head>
        <body>
          <div class="header-container">
            <img src="${baseUrl}/images/Logotipo-RX-big.png" alt="RedXtreme" />
            <h2>${title}</h2>
          </div>
          <div id="print-content">Renderizando formato...</div>
          <script>
            const rawMarkdown = \`${safeContent}\`;
            document.getElementById('print-content').innerHTML = marked.parse(rawMarkdown);
            setTimeout(() => { window.print(); window.close(); }, 1000);
          </script>
        </body>
      </html>
    `);
    printWin.document.close();
  };

  // Cargar datos iniciales
  useEffect(() => {
    if (id) {
      loadSale();
    }
  }, [id, loadSale]);

  if (loading) {
    return (
      <div className="animate-fadeIn">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="loading-spinner"></div>
          <p className="mt-4 text-gray-600">Cargando venta...</p>
        </div>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="animate-fadeIn">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Venta no encontrada</h3>
          <p className="text-gray-600 mb-6">La venta que buscas no existe o ha sido eliminada</p>
          <button
            onClick={() => navigate('/sales')}
            className="btn btn-primary"
          >
            <ArrowLeft size={16} />
            Volver a Ventas
          </button>
        </div>
      </div>
    );
  }

  const StatusIcon = getSaleStatus(sale.status).icon;
  const statusConfig = getSaleStatus(sale.status);
  const paymentStatusConfig = getPaymentStatus(sale.paymentStatus);
  const isConfirmed = sale.status === 'CONFIRMED' || sale.status === 'COMPLETED';

  // Cálculo de progreso de pago para validaciones de proyecto
  const totalPaid = sale.invoice?.totalPaid || 0;
  const totalAmount = sale.total || 0;
  const paymentPercent = totalAmount > 0 ? (totalPaid / totalAmount) * 100 : 0;
  
  // Umbral requerido: Abono inicial pactado o 20% como salvaguarda
  const requiredPercent = sale.paymentPlan?.details?.initialPaymentPercent || 20;
  const canGenerateProject = isConfirmed && sale.proposalId && !sale.hasProject && (paymentPercent >= requiredPercent || sale.paymentStatus === 'PAID');

  return (
    <div className="animate-fadeIn">
      {/* Header Integrado con Datos del Cliente */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full md:w-auto">
          <button
            onClick={() => navigate('/sales')}
            className="btn btn-outline btn-sm self-start sm:self-center"
          >
            <ArrowLeft size={16} />
            Volver
          </button>
          
          <div className="flex items-center gap-4">
             <div className="hidden sm:flex w-12 h-12 bg-blue-100 text-blue-700 rounded-lg justify-center items-center font-bold text-xl border border-blue-200">
                {sale.client?.name?.charAt(0).toUpperCase() || 'C'}
             </div>
             <div>
               <div className="flex items-center gap-2 mb-1">
                 <ShoppingCart className="text-green-600" size={20} />
                 <h1 className="text-2xl font-bold text-gray-900 m-0 leading-none">
                   Venta #{sale.saleNumber || sale._id.slice(-6)}
                 </h1>
               </div>
               <div className="text-sm">
                 <span className="font-bold text-gray-800 mr-2">{sale.client?.name}</span>
                 <span className="text-gray-500 hidden md:inline-block">| {sale.client?.email} | 📞 {sale.client?.phone}</span>
               </div>
             </div>
          </div>
        </div>

        <div className="flex gap-2 w-full md:w-auto justify-end">
          {!isConfirmed && sale.status !== 'CANCELLED' && (
            <>
              <button
                onClick={handleConfirmSale}
                className={`btn ${missingDocs.length === 0 ? 'btn-success' : 'btn-outline'}`}
                title={missingDocs.length > 0 ? 'Faltan documentos' : 'Confirmar venta'}
              >
                <CheckCircle size={16} />
                Confirmar Venta
              </button>
              <button
                onClick={() => navigate(`/sales/edit/${id}`)}
                className="btn btn-sm"
                style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: 'white' }}
              >
                <Edit size={16} />
                Editar
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger btn-sm"
              >
                <Trash2 size={16} />
                Cancelar
              </button>
            </>
          )}
          
          {isConfirmed && (
            <div className="flex gap-2">
              {/* Botón Inteligente de Generación de Proyecto */}
              {sale.proposalId && !sale.hasProject && (
                <button
                  onClick={async () => {
                    if (!canGenerateProject) {
                      showNotification(`No se puede generar: Se requiere cubrir el abono inicial (${requiredPercent}%). Actual: ${paymentPercent.toFixed(1)}%`, 'warning');
                      return;
                    }
                    try {
                      setTriggerLoading(true);
                      const result = await projectService.triggerProjectCreation(sale._id);
                      
                      if (result.status === 'CREATED') {
                        showNotification('✅ Proyecto generado exitosamente.', 'success');
                      } else if (result.status === 'ALREADY_EXISTS') {
                        showNotification('ℹ️ El proyecto ya existía para esta venta.', 'info');
                      }
                      
                      // Navegar al proyecto tras un breve delay
                      if (result.project?._id) {
                        setTimeout(() => navigate(`/projects/${result.project._id}`), 1500);
                      }
                    } catch (error) {
                      showNotification(error.message || 'Error al generar proyecto', 'error');
                    } finally {
                      setTriggerLoading(false);
                    }
                  }}
                  className={`btn btn-sm ${canGenerateProject ? 'btn-primary shadow-sm' : 'bg-gray-200 text-gray-500 cursor-not-allowed border-gray-300'}`}
                  disabled={triggerLoading}
                  title={canGenerateProject ? "Activar proyecto ahora" : `Falta completar el pago inicial (${requiredPercent}%)`}
                >
                  {triggerLoading ? <span className="loading-spinner w-4 h-4 mr-2"></span> : <Sparkles size={16} className="mr-2" />}
                  {triggerLoading ? 'Generando...' : 'Generar Proyecto'}
                </button>
              )}

              {/* Acceso Directo si ya existe */}
              {sale.hasProject && sale.projectId && (
                <button
                  onClick={() => navigate(`/projects/${sale.projectId}`)}
                  className="btn btn-success btn-sm flex items-center gap-2 shadow-sm"
                >
                  <Eye size={16} /> Ver Proyecto
                </button>
              )}
              <button
                onClick={() => navigate(`/sales/new?clone_from=${id}`)}
                className="btn btn-outline btn-sm"
                title="Crear copia de esta venta"
              >
                <Copy size={16} />
                Clonar Venta
              </button>
              <button
                className="btn btn-primary btn-sm flex items-center gap-1.5 shadow-sm font-semibold"
                onClick={() => printSaleDocument(sale)}
              >
                <Printer size={16} />
                Imprimir Orden / PDF
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        
        {/* Columna Izquierda (Servicios, Facturación, Documentos, IA) - 2/3 de ancho */}
        <div style={{ flex: '2 1 600px' }} className="space-y-6">
          
          {/* Bloque 1: Resumen de Servicios */}
          <div className="card">
            <div className="card-header bg-gray-50 flex items-center justify-between">
              <h3 className="card-title flex items-center gap-2 m-0 mt-3 text-lg">
                <Package className="text-green-600" size={20} />
                Servicios Adquiridos
              </h3>
              <span className="badge badge-primary">{sale.items?.length || 0} Items</span>
            </div>
            <div className="card-content">
              <div className="space-y-3">
                {sale.items?.map((item, index) => (
                  <div key={index} className="flex flex-col md:flex-row md:items-center justify-between p-3 bg-white border border-gray-100 shadow-sm rounded-lg hover:border-gray-300 transition-colors">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{item.service ? item.service.name : item.description}</h4>
                      {item.service && item.service.description && (
                        <p className="text-sm text-gray-500 mt-1">{item.service.description}</p>
                      )}
                      {!item.service && (
                        <p className="text-xs text-blue-600 font-medium mt-1 bg-blue-50 w-max px-2 py-0.5 rounded">Producto Premium IA</p>
                      )}
                    </div>
                    <div className="mt-2 md:mt-0 md:text-right border-t md:border-t-0 pt-2 md:pt-0">
                      <div className="text-sm text-gray-500">
                        {item.quantity} un. x ${item.unitPrice?.toLocaleString()}
                      </div>
                      <div className="font-bold text-gray-900 text-lg">
                        ${item.totalPrice?.toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bloque 2: Configurador PRO de Facturación (Movido aquí a petición del usuario) */}
          <div className={`card ${!isConfirmed ? 'border-blue-300 shadow-sm' : 'border-gray-200'} transition-all`} ref={billingSectionRef}>
            <div className="card-header bg-blue-50 flex items-center justify-between">
              <h3 className="card-title text-blue-900 flex items-center gap-2 m-0 mt-3 text-lg font-bold">
                <CreditCard size={20} className="text-blue-600" /> Detalle de Facturación y Método de Pago
              </h3>
            </div>
            
            <div className="card-content p-5 space-y-6">
              {/* Fechas */}
              <div>
                 <span className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Cronograma de Facturación</span>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   <div>
                     <label className="text-sm font-bold text-gray-700 block mb-2">
                        F. Inicio Facturación <span className="text-red-500">*</span>
                     </label>
                     <input type="date" value={billingData.startDate} onChange={(e) => setBillingData(prev => ({ ...prev, startDate: e.target.value }))} className="form-input text-sm py-2 px-3 w-[80%] rounded-md cursor-pointer border border-gray-300 focus:border-blue-500 transition-colors bg-gray-50" disabled={isConfirmed} />
                   </div>
                   <div>
                     <label className="text-sm font-bold text-gray-700 block mb-2 flex justify-between w-[80%]">
                        F. Cierre Proyectado <span className="text-gray-400 font-normal">Opcional</span>
                     </label>
                     <input type="date" value={billingData.endDate} onChange={(e) => setBillingData(prev => ({ ...prev, endDate: e.target.value }))} className="form-input text-sm py-2 px-3 w-[80%] rounded-md cursor-pointer border border-gray-300 focus:border-blue-500 transition-colors bg-gray-50" disabled={isConfirmed} />
                   </div>
                 </div>
              </div>

              {/* Plan de Pagos */}
              <div className="pt-5 border-t border-gray-100">
                <span className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">División Comercial y Régimen de Pagos</span>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {['100%', '50/50', '40/30/30', 'CUSTOM'].map(type => {
                     const isSelected = paymentPlan.type === type;
                     return (
                      <button 
                        key={type} 
                        onClick={() => !isConfirmed && handlePaymentPlanChange(type)} 
                        disabled={isConfirmed} 
                        className={`btn btn-sm ${isSelected ? 'btn-primary shadow-md transform scale-105' : 'btn-outline bg-gray-50'}`}
                      >
                        {type === 'CUSTOM' ? 'Pago a Cuotas' : type}
                      </button>
                     )
                  })}
                </div>

                {showCustomPlan && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-4 shadow-inner" style={{ display: 'flex', flexWrap: 'wrap', gap: '32px' }}>
                     {/* Abono Inicial Dinámico */}
                     <div style={{ flex: '1 1 300px' }}>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Abono Inicial (Anticipo)</label>
                        <div style={{ display: 'flex', gap: '12px' }}>
                           <div style={{ position: 'relative', flex: '1' }}>
                              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontWeight: 'bold' }}>%</span>
                              <input 
                                type="number" 
                                min="0" 
                                max="100" 
                                value={paymentPlan.details.initialPaymentPercent === 0 ? '' : paymentPlan.details.initialPaymentPercent} 
                                onChange={e => {
                                   if (e.target.value === '') {
                                     setPaymentPlan(p => ({...p, details: {...p.details, initialPaymentPercent: 0}}));
                                     return;
                                   }
                                   let val = parseInt(e.target.value);
                                   if (isNaN(val)) return;
                                   if (val > 100) val = 100;
                                   setPaymentPlan(p => ({...p, details: {...p.details, initialPaymentPercent: val}}));
                                }} 
                                className="form-input" 
                                style={{ paddingLeft: '32px', textAlign: 'right', fontSize: '1.125rem', fontWeight: 'bold', width: '100%' }}
                                disabled={isConfirmed}
                              />
                           </div>
                           <div style={{ position: 'relative', flex: '1.5' }}>
                              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontWeight: 'bold' }}>$</span>
                              <input 
                                type="text" 
                                value={paymentPlan.details.initialPaymentPercent === 0 ? '' : Math.round((sale.total * paymentPlan.details.initialPaymentPercent) / 100).toLocaleString('en-US')} 
                                onChange={e => {
                                   let rawValue = e.target.value.replace(/[^\d]/g, '');
                                   if (rawValue === '') {
                                     setPaymentPlan(p => ({...p, details: {...p.details, initialPaymentPercent: 0}}));
                                     return;
                                   }
                                   let val = parseInt(rawValue);
                                   if (isNaN(val)) return;
                                   if (val > sale.total) val = sale.total;
                                   let percent = sale.total > 0 ? Math.round((val / sale.total) * 100) : 0;
                                   setPaymentPlan(p => ({...p, details: {...p.details, initialPaymentPercent: percent}}));
                                }} 
                                className="form-input" 
                                style={{ paddingLeft: '32px', textAlign: 'right', fontSize: '1.125rem', fontWeight: 'bold', width: '100%' }}
                                disabled={isConfirmed}
                              />
                           </div>
                        </div>
                     </div>
                     {/* Cuotas Restantes */}
                     <div style={{ flex: '1 1 200px' }}>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Cant. Cuotas Restantes</label>
                        <input 
                          type="number" 
                          min="1" 
                          value={paymentPlan.details.installments || ''} 
                          onChange={e => {
                             if (e.target.value === '') {
                               setPaymentPlan(p => ({...p, details: {...p.details, installments: ''}}));
                               return;
                             }
                             let val = parseInt(e.target.value);
                             if (isNaN(val)) return;
                             setPaymentPlan(p => ({...p, details: {...p.details, installments: val}}));
                          }} 
                          className="form-input" 
                          style={{ textAlign: 'center', fontSize: '1.125rem', fontWeight: 'bold', maxWidth: '200px' }}
                          disabled={isConfirmed}
                        />
                     </div>
                  </div>
                )}

                {/* Resumen Final Resultante */}
                <div className="bg-white rounded-lg p-5 border-2 border-gray-100 mt-4">
                  <div className="flex justify-between items-center font-medium text-gray-700 mb-2">
                     <span className="text-sm">Fase 1 (Abono Inicial):</span>
                     <span className="font-bold text-lg text-gray-900">${(sale.total * paymentPlan.details.initialPaymentPercent / 100).toLocaleString('en-US')}</span>
                  </div>
                  {paymentPlan.details.installments > 0 && paymentPlan.details.initialPaymentPercent < 100 && (
                     <div className="flex justify-between items-center font-medium text-gray-700 border-t border-gray-200 border-dashed pt-3 mt-1">
                        <span className="text-sm">Fase 2 ({paymentPlan.details.installments} Cuotas Restantes):</span>
                        <span className="font-bold text-lg text-blue-600">${(sale.total * (100 - paymentPlan.details.initialPaymentPercent) / 100 / paymentPlan.details.installments).toLocaleString('en-US', {maximumFractionDigits:0})} × {paymentPlan.details.installments} cuotas</span>
                     </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bloque 3: Documentación Requerida */}
          {requiredDocs.length > 0 && (
            <div 
              className={`card ${missingDocs.length > 0 && !isConfirmed ? 'border-orange-200 shadow-sm' : ''}`} 
              ref={documentsSectionRef}
            >
              <div className="card-header flex justify-between items-center bg-gray-50">
                <h3 className="card-title flex items-center gap-2 m-0 mt-3 text-lg">
                  <FileText className="text-orange-500" size={20} />
                  Anexos y Documentación
                </h3>
                {!isConfirmed && missingDocs.length > 0 && (
                  <span className="text-xs font-bold text-orange-700 bg-orange-100 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-sm mt-3">
                    <AlertTriangle size={14} /> Faltan {missingDocs.length}
                  </span>
                )}
                {!isConfirmed && missingDocs.length === 0 && (
                  <span className="text-xs font-bold text-green-700 bg-green-100 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-sm mt-3">
                    <CheckCircle size={14} /> Completo
                  </span>
                )}
              </div>
              <div className="card-content">
                <p className="text-sm text-gray-500 mb-5">
                  {isConfirmed 
                    ? "Anexos adjuntos exitosamente a esta orden operada." 
                    : "⚠️ Obligatorio subir la base documental antes de confirmar facturación operativa."}
                </p>
                
                <div className="space-y-3">
                  {requiredDocs.map(docType => {
                    const uploadedDoc = sale.documents?.find(d => d.type === docType);
                    const isMissing = !uploadedDoc;
                    
                    return (
                      <div key={docType} className={`flex items-center justify-between p-4 border rounded-xl transition-all ${isMissing && !isConfirmed ? 'bg-orange-50/50 border-orange-200' : 'bg-white border-gray-100 hover:border-gray-300 shadow-sm'}`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex justify-center items-center shrink-0 ${isMissing ? 'bg-orange-100' : 'bg-green-100 text-green-600'}`}>
                            {isMissing ? <AlertTriangle size={20} className="text-orange-400" /> : <CheckCircle size={20} />}
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 text-sm leading-tight">{docTypeLabels[docType]}</h4>
                            {uploadedDoc && (
                              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <span className="truncate max-w-[150px]">{uploadedDoc.filename}</span> • {new Date(uploadedDoc.uploadDate).toLocaleDateString()}
                              </p>
                            )}
                            {isMissing && (
                              <p className="text-xs text-orange-600 font-bold mt-1">Archivo PDF / JPG Requerido</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-2 shrink-0">
                          {uploadedDoc && (
                            <a href={`${API_BASE_URL}${uploadedDoc.url}`} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm h-9 px-3">
                              <Eye size={16} /> Ver
                            </a>
                          )}
                          {!isConfirmed && (
                            <div className="relative">
                              <input type="file" id={`upload-${docType}`} className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload(e, docType)} disabled={uploadingDoc === docType} />
                              <label htmlFor={`upload-${docType}`} className={`btn btn-sm h-9 px-4 cursor-pointer flex items-center gap-2 ${isMissing ? 'btn-primary' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-transparent hover:border-gray-300'}`}>
                                {uploadingDoc === docType ? <span className="loading-spinner w-4 h-4"></span> : <Upload size={16} />}
                                {uploadedDoc ? 'Remplazar' : 'Subir Documento'}
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Bloque 4: Documentos de Propuesta Inteligente IA (Solo si existe) */}
          {proposal && (
            <div className="card border-l-4 border-l-orange-400">
              <div className="card-header bg-orange-50 border-b border-orange-100">
                <h3 className="card-title flex items-center gap-2 text-orange-900 m-0 mt-3 text-lg">
                  <Sparkles className="text-orange-500" size={20} />
                  Activos de la Propuesta Inteligente
                </h3>
              </div>
              <div className="card-content">
                <div className="grid grid-cols-1 gap-4">
                  {proposal.commercialDoc && (
                    <div className="bg-white border border-orange-200 p-5 rounded-xl flex flex-col md:flex-row justify-between items-center text-center md:text-left gap-4 hover:shadow-md transition-shadow">
                      <div className="flex flex-col md:flex-row items-center gap-4">
                        <div className="w-12 h-12 bg-orange-100 text-orange-500 rounded-full flex items-center justify-center shrink-0">
                           <FileText size={24} />
                        </div>
                        <div>
                           <h4 className="font-bold text-gray-900 text-base m-0 mb-1">Cotización Externa Oficial</h4>
                           <p className="text-xs text-gray-600 m-0">El contrato exacto avalado y presentado por IA hacia el comprador.</p>
                        </div>
                      </div>
                      <button onClick={() => printMarkdownDocument(`Propuesta Comercial: ${proposal.title}`, proposal.commercialDoc)} className="btn hover:bg-orange-600 bg-orange-500 text-white border-transparent px-6 rounded-xl shadow-sm shrink-0 whitespace-nowrap">
                        🖨️ Imprimir PDF Oficial
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notas */}
          {sale.notes && (
            <div className="card">
               <div className="card-header">
                  <h3 className="card-title flex items-center gap-2 m-0 mt-3">
                     <FileText size={20} className="text-gray-400"/> Observaciones Operativas
                  </h3>
               </div>
               <div className="card-content">
                  <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed m-0 p-4 bg-gray-50 rounded-lg border border-gray-100">{sale.notes}</p>
               </div>
            </div>
          )}

        </div>

        {/* Columna Derecha (Finanzas y Resumen) - 1/3 de ancho */}
        <div style={{ flex: '1 1 300px' }} className="space-y-6">
          
          {/* Tarjeta 1: Total Financiero Monumental */}
          <div className="card border border-green-200 overflow-hidden shadow-sm shadow-green-100/50">
            <div className={`p-6 border-b ${isConfirmed ? 'bg-gradient-to-br from-green-500 to-green-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
              <div className="flex justify-between items-start mb-2">
                 <p className="text-xs font-bold opacity-80 tracking-widest uppercase m-0 flex items-center gap-1.5"><DollarSign size={14}/> Inversión Final</p>
                 <span className={`px-2.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${isConfirmed ? 'bg-white/20' : 'bg-gray-200 text-gray-600'}`}>
                    <StatusIcon size={10} className="inline mr-1 -mt-0.5"/> {statusConfig.label}
                 </span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-black mt-2 m-0 tracking-tighter">${sale.total?.toLocaleString() || '0'}</h2>
            </div>
            
            <div className="p-0 border-b border-gray-100 bg-white">
               <div className="flex divide-x divide-gray-100">
                  <div className="flex-1 p-4 text-center hover:bg-gray-50 transition-colors">
                     <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Subtotal Bruto</span>
                     <span className="font-bold text-gray-800 text-lg">${sale.subtotal?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex-1 p-4 text-center hover:bg-gray-50 transition-colors">
                     <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Impuestos</span>
                     <span className="font-bold text-gray-800 text-lg">${sale.tax?.toLocaleString() || '0'}</span>
                  </div>
               </div>
            </div>

            <div className="card-content text-center flex flex-col items-center justify-center p-5 bg-gray-50/50">
               <span className="text-[10px] font-bold text-gray-500 mb-1.5 uppercase tracking-widest">Liquidación efectuada vía</span>
               <span className={`inline-flex items-center justify-center px-4 py-1.5 rounded-full text-xs font-black tracking-wide border shadow-sm ${paymentStatusConfig.bg} ${paymentStatusConfig.color} ${paymentStatusConfig.border ? paymentStatusConfig.border : 'border-transparent'}`}>
                 💳 {getPaymentMethod(sale.paymentMethod)}
               </span>
               <div className="text-[10px] text-gray-400 mt-4">Registrada el: {new Date(sale.createdAt).toLocaleDateString()} a las {new Date(sale.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Sistema de notificaciones */}
      <NotificationSystem 
        notification={notification} 
        onClose={hideNotification} 
      />
    </div>
  );
};

export default SaleDetailPage;